import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Box, CircularProgress } from '@mui/material';
import { ReactComponent as Blazon } from '../images/blazon.svg';
import { FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import '../css/HomeStyles.css';

function Home() {
    const [msgNumber, setMsgNumber] = useState('');
    const [platoNumber, setPlatoNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleMsgNumberChange = (e) => {
        try {
            const input = e.target.value.toUpperCase();
            setMsgNumber(input);
        } catch (error) {
            console.warn('Error in handleMsgNumberChange:', error);
        }
    };

    const handleMsgNumberAutoFill = (e) => {
        try {
            // Handle autofill events
            const input = e.target.value.toUpperCase();
            setMsgNumber(input);
        } catch (error) {
            console.warn('Auto-fill error for msgNumber:', error);
        }
    };

    const transliteratePlatoNumber = (input) => {
        const translitMap = {
            'А': 'A', 'В': 'B', 'Е': 'E', 'І': 'I', 'К': 'K', 'М': 'M',
            'Н': 'H', 'О': 'O', 'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'Y', 'Х': 'X',
        };
        return input.replace(/[А-Я]/g, (char) => translitMap[char] || char);
    };

    const handlePlatoNumberChange = (e) => {
        try {
            const input = e.target.value.toUpperCase();
            const transliterated = transliteratePlatoNumber(input);
            setPlatoNumber(transliterated);
        } catch (error) {
            console.warn('Error in handlePlatoNumberChange:', error);
        }
    };

    const handlePlatoNumberAutoFill = (e) => {
        try {
            // Handle autofill events
            const input = e.target.value.toUpperCase();
            const transliterated = transliteratePlatoNumber(input);
            setPlatoNumber(transliterated);
        } catch (error) {
            console.warn('Auto-fill error for platoNumber:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const numericMsgNumber = msgNumber.replace(/\D/g, '');
            const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
            //const baseUrl = 'http://127.0.0.1:3000';
            const response = await axios.post(`${baseUrl}/api/getFine`, { msgNumber: numericMsgNumber, platoNumber });
            // Перенаправляємо на сторінку з параметрами URL для підтримки оновлення сторінки
            navigate(`/details/${numericMsgNumber}/${platoNumber}`, { state: { data: response.data } });
        } catch (error) {
            console.error("Error fetching data:", error);
            setError('Не вдалося завантажити дані. Спробуйте ще раз.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="false" className="container">
            <div className="form-container">
                <div className="icon">
                    <Blazon width={80} height={80} />
                </div>

                {/* Заголовок */}
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#333', fontFamily: 'e-UkraineHead' }}>
                    Сплата штрафу онлайн
                </Typography>

                {/* Підзаголовок */}
                <Typography variant="body1" sx={{ mb: 3, color: '#555', fontFamily: 'e-Ukraine' }}>
                    Введіть дані, щоб перевірити наявність штрафу
                </Typography>

                <form onSubmit={handleSubmit}>
                    <div className="input-field">
                        <label htmlFor="msgNumber">Серія та номер постанови</label>
                        <input
                            type="text"
                            id="msgNumber"
                            value={msgNumber}
                            onChange={handleMsgNumberChange}
                            onInput={handleMsgNumberAutoFill}
                            onBlur={handleMsgNumberAutoFill}
                            placeholder="Введіть серію та номер"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            required
                        />
                    </div>

                    <div className="input-field">
                        <label htmlFor="platoNumber">Державний номерний знак</label>
                        <input
                            type="text"
                            id="platoNumber"
                            value={platoNumber}
                            onChange={handlePlatoNumberChange}
                            onInput={handlePlatoNumberAutoFill}
                            onBlur={handlePlatoNumberAutoFill}
                            placeholder="Введіть номер знаку"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            required
                        />
                    </div>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <button type="submit" className="gradient-button" disabled={loading}>
                            {loading ? (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                    <CircularProgress size={18} color="inherit" /> Завантаження...
                                </span>
                            ) : (
                                'Перевірити'
                            )}
                        </button>
                        <Typography variant="body2" sx={{ mt: 1, color: '#777', fontSize: '14px' }}>
                            Натискаючи на кнопку <strong>"Перевірити"</strong>, ви приймаєте
                            <a href="/public_offer.pdf" target="_blank" style={{ color: '#007bff', textDecoration: 'none' }}> Угоду користувача</a>.
                        </Typography>
                        {error && (
                            <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f', fontSize: '14px' }}>
                                {error}
                            </Typography>
                        )}
                    </Box>
                </form>
            </div>

            {/* Секції на білому фоні */}
            <div className="section-wrapper">
                <div className="section">
                    <div className="section-item">
                        <h3>Зручний перегляд штрафів</h3>
                        <p>Ми надаємо детальну інформацію про всі ваші штрафи в одному місці, включаючи їх статус, дату правопорушення та суму.</p>
                    </div>
                    <div className="section-item">
                        <h3>Мінімальна комісія</h3>
                        <p>Наш сервіс пропонує найнижчу комісію для оплати штрафів, щоб ви не переплачували за адміністративні витрати.</p>
                    </div>
                    <div className="section-item">
                        <h3>Швидка обробка платежів</h3>
                        <p>Оплата штрафу відбувається миттєво, і ми гарантуємо, що гроші надійдуть безпосередньо до державного казначейства.</p>
                    </div>
                    <div className="section-item">
                        <h3>Безпека та надійність</h3>
                        <p>Ваші особисті дані та фінансова інформація захищені сучасними технологіями шифрування, що забезпечує максимальну безпеку транзакцій.</p>
                    </div>
                    <div className="section-item">
                        <h3>Пільговий період</h3>
                        <p>Якщо ви в пільговому періоді (перші 10 днів), ви можете сплатити знижку 50% від повної суми штрафу. Суму ви можете вибрати самостійно.</p>
                    </div>
                    <div className="section-item">
                        <h3>Простота використання</h3>
                        <p>Вам не потрібно запам'ятовувати реквізити чи шукати інші способи оплати — весь процес автоматизований і доступний за кілька кліків.</p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-content">
                    <div className="footer-left">
                        <h2 className="footer-brand">PayFine</h2>
                        <p className="footer-description">Офіційний сервіс сплати штрафів</p>
                        <p className="footer-agreement">
                            <a href="/public_offer.pdf" target="_blank">Публічний договір</a>
                        </p>
                    </div>

                    <div className="footer-center">
                        <p>
                            <FaMapMarkerAlt />
                            <a href="https://maps.google.com/?q=м. Львів, вул. Водогінна, буд. 2А, офіс 62"
                               target="_blank"
                               rel="noopener noreferrer">
                                 м. Львів, вул. Водогінна, буд. 2А, офіс 62
                            </a>
                        </p>

                        <p>
                            <FaEnvelope />
                            <a href="mailto:support@payfine.com.ua">
                                 support@payfine.com.ua
                            </a>
                        </p>
                    </div>

                    <div className="footer-right">
                        <div className="social-icons">
                            {/* <a href="https://facebook.com" target="_blank" className="social-icon"><FaFacebook /></a> */}
                            {/* <a href="https://t.me" target="_blank" className="social-icon"><FaTelegram /></a> */}
                        </div>
                    </div>
                </div>

            </footer>
        </Container>
    );
}

export default Home;